import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import Seo, { ORG_JSONLD } from '../components/Seo';
import { Button } from '../components/ui/button';
import { Rocket, Users2, Globe2, ShieldCheck, HeartHandshake, Lightbulb } from 'lucide-react';

const TIMELINE = [
  { year: '2015', title: 'Founded in Pune', text: 'Hipro Technologies starts as a 4-person web studio with one promise: ship work we are proud of.' },
  { year: '2017', title: 'First international clients', text: 'Projects in the UK and Singapore open the door to global delivery.' },
  { year: '2019', title: '100+ projects milestone', text: 'Dedicated teams model launches; Hyderabad and Bangalore offices open.' },
  { year: '2021', title: 'Enterprise & cloud practice', text: 'ERP, cloud engineering and 24/7 managed services join the portfolio.' },
  { year: '2023', title: 'AI & Automation division', text: 'Practical AI delivery — chatbots, document intelligence and ML — becomes a core offering.' },
  { year: '2026', title: '1000+ projects · 1800+ professionals', text: 'Nine offices across India, USA and UK. Innovate. Build. Scale.' },
];

const VALUES = [
  { Icon: Rocket, t: 'Outcomes over output', d: 'We measure success in your metrics — revenue, savings, adoption — not hours billed.' },
  { Icon: ShieldCheck, t: 'Own the hard parts', d: 'Security, scale and compliance are designed in from day one, never bolted on.' },
  { Icon: HeartHandshake, t: 'Radical transparency', d: 'Weekly demos, open backlogs and honest estimates — even when the news is hard.' },
  { Icon: Lightbulb, t: 'Practical innovation', d: 'New tech earns its place by solving a real problem, not by being fashionable.' },
];

const STATS = [
  { v: '1000+', l: 'Projects Delivered' },
  { v: '1800+', l: 'Technology Professionals Worldwide' },
  { v: '800+', l: 'Clients Served' },
  { v: '9', l: 'Offices · India, USA & UK' },
];

export default function AboutUsPage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="About Hipro Technologies — Innovate. Build. Scale"
        description="Since 2015, Hipro Technologies has grown from a 4-person Pune studio into a global IT services company: 1000+ projects, 1800+ professionals, 9 offices across India, USA and UK."
        path="/about-us"
        jsonLd={ORG_JSONLD}
      />
      <Navbar />
      <section className="pt-28 pb-14 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Our Story</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold">Built to make ambitious companies unstoppable</h1>
          <p className="mt-5 text-slate-300 leading-relaxed">What started in 2015 as a four-person studio in Pune is today a 1800+ strong global engineering company. One thing never changed: we treat every product like our own and every client like a partner.</p>
        </div>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.l} className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
              <div className="text-3xl font-extrabold text-orange-400">{s.v}</div>
              <div className="mt-1 text-xs text-slate-400">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-center">A decade of momentum</h2>
          <div className="mt-10 relative border-l-2 border-orange-200 ml-3 space-y-10" data-testid="company-timeline">
            {TIMELINE.map((m) => (
              <div key={m.year} className="relative pl-8">
                <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-orange-500 ring-4 ring-orange-100" />
                <div className="text-xs font-extrabold text-orange-600">{m.year}</div>
                <div className="mt-1 font-bold text-slate-900">{m.title}</div>
                <p className="mt-1 text-sm text-slate-600">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-center">What we stand for</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map(({ Icon, t, d }) => (
              <div key={t} className="rounded-2xl border bg-white p-6">
                <Icon className="h-6 w-6 text-orange-500" />
                <div className="mt-3 font-bold">{t}</div>
                <p className="mt-1.5 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Globe2 className="h-8 w-8 text-orange-500 mx-auto" />
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold">One team, three continents</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Follow-the-sun delivery from Pune, Hyderabad, Bangalore, Gurgaon, New York and Waltham Cross — with timezone overlap guaranteed for every client.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={() => nav('/book-meeting')} data-testid="about-cta-btn" className="bg-orange-500 hover:bg-orange-600 h-11 px-6 rounded-full">Work with us</Button>
            <Button onClick={() => nav('/careers')} variant="outline" className="h-11 px-6 rounded-full">Join the team <Users2 className="h-4 w-4 ml-1.5" /></Button>
          </div>
        </div>
      </section>
      <Footer />
      <ChatWidget />
    </div>
  );
}
